import { QueryCache, QueryClient } from '@tanstack/react-query'
import type { Persister } from '@tanstack/react-query-persist-client'
import { authMeKey } from '@/auth/keys'
import { HttpError } from '@/lib/http'
import { appPersister, PERSIST_MAX_AGE } from '@/lib/query-persist'

/**
 * Retry the failures a retry can fix, and only those.
 *
 * The default is three retries with exponential backoff for everything, which
 * means a 429 or a 401 — a considered answer from a server that is up — is
 * asked again three times before the reader is told anything. Ten to twenty
 * seconds of skeletons for a refusal that was decided in the first ten
 * milliseconds, and on the polling list pages three more requests into a
 * rate limit that is already complaining about the request rate.
 *
 * A 5xx or a dropped connection is worth asking again; a 4xx is the server
 * saying no, and asking again does not change its mind.
 */
function retryTransportOnly(failureCount: number, error: unknown): boolean {
  if (error instanceof HttpError && error.status < 500) return false
  return failureCount < 3
}

/**
 * The app's query client.
 *
 * - `staleTime` 30s: a page opened twice in a row shows the answer it has
 *   instead of asking again at once.
 * - `gcTime` 24h: at least the persisted cache's age, or a restored query would
 *   be collected before anything reads it.
 * - A 401 on ANY query means the session may be gone: ask who is signed in
 *   again. The guard sends a signed-out answer to /login and clears the cache.
 */
export function createAppQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof HttpError && error.status === 401) {
          void client.invalidateQueries({ queryKey: authMeKey })
        }
      },
    }),
    defaultOptions: {
      queries: { retry: retryTransportOnly, staleTime: 30_000, gcTime: PERSIST_MAX_AGE },
    },
  })
  return client
}

/** The app's one query client. */
export const appQueryClient = createAppQueryClient()

/**
 * Forget every piece of server state, in memory and on disk. For a sign-out, a
 * session that turned out to be gone, and another tab signing out.
 */
export async function forgetServerState(
  client: QueryClient = appQueryClient,
  persister: Persister = appPersister,
): Promise<void> {
  client.clear()
  await persister.removeClient()
}
